<template>
  <teleport to="body">
    <transition-fade :duration="200">
      <div
        v-if="show"
        class="q-modal__backdrop"
        :class="backdrop ? `-${backdrop}` : ''"
        @click="dismissable ? $emit('close') : null"
        @keydown="!propagateEvents ? $event.stopPropagation() : null"
      >
        <dialog
          v-show="show"
          ref="dialog"
          :open="show"
          class="q-modal focus-visible:outline-none"
          :class="[
            $attrs.class,
            embeddedRuntime() && 'q-modal--embedded',
            {
              '-wide': wide,
              '-extraWide': extraWide,
              '-full': full,
              '-narrow': narrow,
              '-extraNarrow': extraNarrow,
            },
          ]"
          v-bind="$attrs"
          tabindex="-1"
        >
          <global-events no-filtering @keyup.esc="handleEscape" />
          <div
            class="q-modal__wrapper"
            :class="[
              {
                'q-modal__wrapper-max-height': scrollable,
                'q-modal__wrapper-min-height': fixedWrapperHeight,
                'p-0': headerDivider || noContentPadding,
              },
              wrapperClass,
            ]"
            @click.stop
          >
            <header
              v-if="title || $slots.header"
              :class="{
                'q-modal__header text-lg font-medium': title,
                'z-10 rounded-t-lg p-8 pb-4 shadow-sm': headerDivider,
              }"
            >
              <slot v-if="$slots.header" name="header" />
              <div v-else>
                {{ title }}
                <p class="q-modal__subtitle text-sm">
                  <slot name="subtitle">{{ subtitle }}</slot>
                </p>
              </div>
              <button
                v-if="closeButton"
                type="button"
                class="q-modal__wrapper__closeBtn flex items-center justify-center"
                @click="closeModal"
              >
                <x-icon class="icon-m" />
              </button>
            </header>

            <section
              class="q-modal__content"
              :class="[
                {
                  '!mt-0': headerDivider,
                  'p-8 pt-5': headerDivider && !noContentPadding,
                  'flex flex-col': flexContent,
                },
                contentClass,
              ]"
            >
              <slot />
            </section>

            <footer
              v-if="$slots.actions"
              :class="[
                'q-modal__footer',
                { 'q-modal__footer-bordered': footerBorder },
                noFooterTopMargin ? 'mt-0' : 'mt-8',
              ]"
            >
              <slot name="actions" />

              <div v-if="hangingContent" class="q-modal__hanging-footer text-xs">
                <span class="q-modal__hanging-content">{{ hangingContent }}</span>
              </div>
            </footer>
          </div>
        </dialog>
      </div>
    </transition-fade>
  </teleport>
</template>

<script setup lang="ts">
import { nextTick, ref, watch, type HTMLAttributes } from 'vue'
import { TransitionFade } from '@morev/vue-transitions/vue3'
import { XIcon } from '@klausapp/ui-kit/icons'
import { GlobalEvents } from '@klausapp/ui-kit'
import analytics from '@/utils/analytics'
import { embeddedRuntime } from '@/config'

const props = defineProps<{
  title?: string
  subtitle?: string
  closeButton?: boolean
  show?: boolean
  dismissable?: boolean
  hangingContent?: string
  propagateEvents?: boolean
  full?: boolean
  wide?: boolean
  extraWide?: boolean
  narrow?: boolean
  extraNarrow?: boolean
  scrollable?: boolean
  backdrop?: 'solid' | 'blurred' | 'transparent'
  footerBorder?: boolean
  headerDivider?: boolean
  noContentPadding?: boolean
  noFooterTopMargin?: boolean
  fixedWrapperHeight?: boolean
  flexContent?: boolean
  wrapperClass?: HTMLAttributes['class']
  contentClass?: HTMLAttributes['class']
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'focus'): void
}>()

defineOptions({
  inheritAttrs: false,
})

const dialog = ref<HTMLDialogElement | null>(null)

const closeModal = () => {
  emit('close')
  if (props.title) analytics.modalClosed(props.title)
}
const handleEscape = (e: KeyboardEvent) => {
  if (props.dismissable && props.show && e.key === 'Escape') {
    closeModal()
  }
}

watch(
  () => props.show,
  async (val) => {
    if (val) {
      await nextTick()
      dialog.value?.focus()
      if (props.title) analytics.modalOpened(props.title)
      emit('focus')
    }
  },
)
</script>

<style lang="scss">
.q-modal {
  --modal-width: 40rem;
  position: absolute;
  right: 0;
  left: 0;
  display: flex;
  min-height: 100%;
  align-items: center;
  margin: 0 auto;
  padding: 0;
  border: none;
  background-color: transparent;
  max-width: var(--modal-width);
  width: 95%;
  transition: height 0.3s;
  color: var(--gray-80);
  outline: 0;

  &:not(.q-modal--embedded) {
    transform: translate3d(calc(var(--navbar-width) / 2), 0, 0);
  }

  &__wrapper {
    display: flex;
    position: relative;
    flex-direction: column;
    margin: 2rem auto 7rem;
    padding: 2rem;
    max-width: var(--modal-width);
    background-clip: padding-box;
    background-color: #fff;
    border-radius: 0.75rem;
    box-shadow: theme('boxShadow.lg');
    transition: height 0.3s;
    outline: 0;

    &__closeBtn {
      position: absolute;
      top: 32px;
      right: 32px;
      width: 24px;
      height: 24px;
      padding: 4px;
      color: var(--gray-60);
      margin-left: auto;
      transition:
        background-color 0.3s ease-out,
        color 0.3s ease-out;
      border-radius: var(--border-radius);

      &:hover {
        color: var(--gray-60);
        background-color: var(--gray-10);
      }
    }
  }

  &__wrapper-max-height {
    max-height: calc(100vh - 64px);

    .q-modal__content {
      overflow-y: auto;
    }
  }

  &__wrapper-min-height {
    min-height: calc(100vh - 64px);
  }

  &.-wide {
    --modal-width: 60rem;
  }
  &.-extraWide {
    --modal-width: 67.5rem;
  }
  &.-narrow {
    --modal-width: 30rem;
  }
  &.-extraNarrow {
    --modal-width: 25rem;
  }
  &.-full {
    --modal-width: 95%;
  }

  .q-modal__wrapper {
    margin: 1.5rem auto 1.5rem;
    flex-basis: var(--modal-width);
  }

  &__backdrop {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background-color: #27334560;
    z-index: 9;
    overflow-y: auto;
    width: 100vw;

    &.-solid {
      background-color: var(--gray-90);
    }
    &.-blurred {
      filter: blur(15px);
    }
    &.-transparent {
      background-color: transparent;
    }
  }

  &__header {
    display: flex;
    align-items: center;
  }

  &__subtitle {
    font-weight: theme('fontWeight.normal');
    margin-top: 8px;
    color: var(--gray-70);

    &:empty {
      margin-top: 0;
    }
  }

  &__trigger {
    margin-left: auto;
    color: var(--gray-60);
  }

  &__content {
    flex: 1;
    color: var(--gray-70);

    &:not(:empty) {
      margin-top: 2rem;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  &__footer-bordered {
    margin: 0 -2rem;
    border-top: 1px solid var(--gray-30);
    padding: 20px 2rem 0 2rem;
  }

  &__hanging-footer {
    text-align: center;
    color: #ffffff;
    position: absolute;
    width: 100%;
    bottom: -2rem;
    left: 0;
    right: 0;
  }
  &__hanging-content {
    background-color: var(--gray-80);
    border-radius: 3px;
    padding: 0.2rem 0.4rem;
  }
}
</style>
